import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Youtube,
  Video,
  Hash,
  FileText,
  Zap,
  List,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init";

const YouTubeGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [videoTopic, setVideoTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [videoType, setVideoType] = useState("Tutorial/How-To");
  const [tone, setTone] = useState("Engaging & Fast-Paced");

  // Output State
  const [results, setResults] = useState({
    titles: [],
    description: "",
    tags: [],
    scriptOutline: ""
  });

  // Options
  const videoTypes = [
    "Tutorial/How-To",
    "Product Review",
    "Vlog/Lifestyle",
    "Educational/Explainer",
    "Commentary/Opinion",
    "Gaming",
    "Shorts (Vertical)"
  ];

  const toneOptions = [
    "Engaging & Fast-Paced",
    "Professional & Authoritative",
    "Clickbait/Hype",
    "Relaxed & Chill",
    "Funny & Witty",
    "Inspirational"
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allyoutube-01-02d59d78beb5.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allyoutube-01-02d59d78beb5.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are a YouTube Growth Expert (MrBeast style retention + SEO specialist).
    Generate a complete video metadata kit for:
    
    Topic: ${videoTopic}
    Keywords: ${keywords}
    Video Type: ${videoType}
    Tone: ${tone}

    Please generate the following 4 sections exactly as requested:

    1. **===TITLES===**
    Generate 5 high-CTR (Click Through Rate) titles. Mix of clickbait and SEO. 
    Format: Just the titles, one per line.

    2. **===DESCRIPTION===**
    Write a 150-200 word SEO-optimized description.
    Include the keywords naturally.
    Include a placeholder section for "Timestamps" and "Links".
    End with a CTA (Call to Action).

    3. **===TAGS===**
    Generate 20 viral, high-volume search tags separated by commas.

    4. **===SCRIPT===**
    Write a brief bullet-point outline (Hook -> Value Prop -> Body Steps -> Conclusion).

    Ensure you use the delimiters ===TITLES===, ===DESCRIPTION===, ===TAGS===, and ===SCRIPT=== to separate sections.`;
  };

  const parseResponse = (text) => {
    const sections = {
      titles: [],
      description: "",
      tags: [],
      scriptOutline: ""
    };

    // Helper regex to extract content between delimiters
    const extract = (key) => {
      const regex = new RegExp(`===${key}===([\\s\\S]*?)(?=(===[A-Z]+===|$))`, 'i');
      const match = text.match(regex);
      return match ? match[1].trim() : "";
    };

    const rawTitles = extract("TITLES");
    sections.titles = rawTitles.split('\n').filter(t => t.trim().length > 0).map(t => t.replace(/^\d+\.\s*/, '').replace(/"/g, ''));

    sections.description = extract("DESCRIPTION");
    
    const rawTags = extract("TAGS");
    sections.tags = rawTags.split(',').map(t => t.trim()).filter(t => t.length > 0);

    sections.scriptOutline = extract("SCRIPT");

    return sections;
  };

  const generateContent = async () => {
    if (!videoTopic.trim()) {
      toast.error("Please enter a video topic");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits.");
      return;
    }

    setGenerating(true);
    setError("");
    setResults({ titles: [], description: "", tags: [], scriptOutline: "" });

    try {
      // 1. Deduct Credit
      const deductRes = await axios.post(`https://allyoutube-01-02d59d78beb5.herokuapp.com/api/deduct-credit`, {
        userEmail: user.email,
        amount: 1,
      });

      if (!deductRes.data.success) throw new Error("Credit deduction failed");
      setCredits(prev => prev - 1);

      // 2. AI Call
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: {
            temperature: 0.85,
            maxOutputTokens: 2048,
          },
        }),
      });

      if (!response.ok) throw new Error("AI Service Unavailable");
      
      const data = await response.json();
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
      
      if (!text) throw new Error("No content generated");

      setResults(parseResponse(text));
      toast.success("YouTube Kit Generated!");

    } catch (err) {
      console.error(err);
      setError(err.message);
      toast.error(err.message);

      // Refund
      try {
        await axios.post(`https://allyoutube-01-02d59d78beb5.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits(prev => prev + 1);
      } catch (e) { console.error("Refund failed", e); }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS
  // ==========================================

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} Copied!`));
  };

  // ==========================================
  // 5. RENDER
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-container">
        <div className="youtube-spinner"></div>
        <p>Loading Creator Studio...</p>
        <style jsx>{`
          .loader-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; background: #f9f9f9; color: #606060; }
          .youtube-spinner { width: 50px; height: 50px; border: 4px solid #e5e5e5; border-top-color: #ff0000; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 15px; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wall">
        <div className="auth-card">
          <Youtube size={48} color="#ff0000" style={{marginBottom: 15}} />
          <h2>Creator Studio Access</h2>
          <p>Login to start generating viral video metadata.</p>
          <button onClick={() => window.location.href = "/login"}>Login Now</button>
        </div>
        <style jsx>{`
          .auth-wall { display: flex; align-items: center; justify-content: center; height: 100vh; background: #f1f1f1; }
          .auth-card { background: white; padding: 2.5rem; border-radius: 16px; box-shadow: 0 10px 25px rgba(0,0,0,0.05); text-align: center; max-width: 400px; width: 90%; }
          .auth-card button { background: #ff0000; color: white; border: none; padding: 12px 24px; border-radius: 4px; font-weight: 600; font-size: 16px; cursor: pointer; margin-top: 15px; }
          .auth-card button:hover { background: #cc0000; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="yt-container">
    

      {/* HEADER */}
      <header className="yt-header">
        <div className="header-content">
          <button className="back-btn" onClick={() => window.history.back()}>&larr; Back</button>
          <div className="logo-area">
            <Youtube size={28} color="#ff0000" fill="#ff0000" />
            <h1>Video Kit Generator</h1>
          </div>
        </div>
        <div className="credits-pill">
          <Zap size={14} fill="currentColor" />
          <span>{credits} Credits</span>
          {credits < 3 && <button onClick={() => window.location.href = "/user/select-package"}>+</button>}
        </div>
      </header>

      <div className="yt-grid">
        {/* LEFT PANEL - INPUTS */}
        <div className="input-section">
          <div className="card input-card">
            <h3><Video size={18} /> Video Details</h3>
            
            <div className="form-group">
              <label>Video Topic / Title Idea</label>
              <textarea 
                value={videoTopic} 
                onChange={(e) => setVideoTopic(e.target.value)} 
                placeholder="e.g. How to bake sourdough bread for beginners..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Target Keywords (SEO)</label>
              <input 
                type="text" 
                value={keywords} 
                onChange={(e) => setKeywords(e.target.value)} 
                placeholder="e.g. sourdough, baking, cooking tutorial"
              />
            </div>

            <div className="row-group">
              <div className="form-group half">
                <label>Video Type</label>
                <select value={videoType} onChange={(e) => setVideoType(e.target.value)}>
                  {videoTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group half">
                <label>Tone</label>
                <select value={tone} onChange={(e) => setTone(e.target.value)}>
                  {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <button 
              className="generate-btn" 
              onClick={generateContent}
              disabled={generating || credits < 1 || !videoTopic.trim()}
            >
              {generating ? "Generating..." : "Generate Video Kit (1 Credit)"}
            </button>
            
            {error && <div className="error-banner"><AlertCircle size={16} /> {error}</div>}
          </div>
        </div>

        {/* RIGHT PANEL - OUTPUTS */}
        <div className="output-section">
          {generating ? (
            <div className="loading-state">
              <div className="pulse-icon"><Youtube size={48} /></div>
              <h3>Analyzing YouTube Algorithm...</h3>
              <p>Crafting high-CTR titles and SEO descriptions.</p>
            </div>
          ) : results.titles.length > 0 ? (
            <div className="results-stack">
              
              {/* TITLES CARD */}
              <div className="card result-card">
                <div className="card-header">
                  <h4><List size={18} /> Viral Title Options</h4>
                  <button onClick={() => copyToClipboard(results.titles.join('\n'), "All Titles")} className="icon-btn"><Copy size={16} /></button>
                </div>
                <div className="titles-list">
                  {results.titles.map((title, idx) => (
                    <div key={idx} className="title-item">
                      <span className="idx">#{idx + 1}</span>
                      <span className="text">{title}</span>
                      <button className="copy-small" onClick={() => copyToClipboard(title, "Title")}>Copy</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* DESCRIPTION CARD */}
              <div className="card result-card">
                <div className="card-header">
                  <h4><FileText size={18} /> SEO Description</h4>
                  <button onClick={() => copyToClipboard(results.description, "Description")} className="icon-btn"><Copy size={16} /></button>
                </div>
                <div className="desc-box">
                  <pre>{results.description}</pre>
                </div>
              </div>

              {/* TAGS CARD */}
              <div className="card result-card">
                <div className="card-header">
                  <h4><Hash size={18} /> Video Tags ({results.tags.length})</h4>
                  <button onClick={() => copyToClipboard(results.tags.join(', '), "Tags")} className="icon-btn"><Copy size={16} /></button>
                </div>
                <div className="tags-cloud">
                  {results.tags.map((tag, i) => (
                    <span key={i} className="tag-chip">{tag}</span>
                  ))}
                </div>
              </div>

              {/* SCRIPT CARD */}
              <div className="card result-card">
                <div className="card-header">
                  <h4><CheckCircle size={18} /> Script Outline</h4>
                  <button onClick={() => copyToClipboard(results.scriptOutline, "Script")} className="icon-btn"><Copy size={16} /></button>
                </div>
                <div className="script-box">
                  <pre>{results.scriptOutline}</pre>
                </div>
              </div>

            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon"><Video size={48} /></div>
              <h3>No Content Generated Yet</h3>
              <p>Enter your video details on the left to generate a full YouTube metadata kit.</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        /* LAYOUT & GLOBAL */
        .yt-container {
          background-color: #f9f9f9;
          min-height: 100vh;
          font-family: 'Roboto', sans-serif;
          color: #0f0f0f;
          padding-bottom: 50px;
        }

        .yt-header {
          background: white;
          padding: 12px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e5e5e5;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .logo-area {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .logo-area h1 {
          font-size: 20px;
          font-weight: 700;
          letter-spacing: -0.5px;
          margin: 0;
        }

        .back-btn {
          border: none;
          background: none;
          color: #606060;
          font-weight: 500;
          cursor: pointer;
          font-size: 14px;
        }

        .credits-pill {
          background: #f2f2f2;
          padding: 6px 12px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #0f0f0f;
        }

        .credits-pill button {
          background: #0f0f0f;
          color: white;
          border: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          line-height: 1;
        }

        /* GRID */
        .yt-grid {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 24px;
          max-width: 1200px;
          margin: 30px auto;
          padding: 0 20px;
        }

        @media (max-width: 900px) {
          .yt-grid { grid-template-columns: 1fr; }
        }

        /* CARDS */
        .card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e5e5;
          padding: 24px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.02);
        }

        /* INPUTS */
        .input-card h3 {
          margin: 0 0 20px 0;
          font-size: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #0f0f0f;
        }

        .form-group { margin-bottom: 16px; }
        .row-group { display: flex; gap: 12px; }
        .half { flex: 1; }

        label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: #606060;
          margin-bottom: 6px;
          text-transform: uppercase;
        }

        input, textarea, select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ccc;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          background: #fff;
          transition: border 0.2s;
        }

        input:focus, textarea:focus, select:focus {
          border-color: #065fd4;
          outline: none;
        }

        .generate-btn {
          width: 100%;
          padding: 12px;
          background: #cc0000;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          margin-top: 10px;
          text-transform: uppercase;
          transition: background 0.2s;
        }

        .generate-btn:hover:not(:disabled) { background: #ff0000; }
        .generate-btn:disabled { background: #e5e5e5; color: #aaa; cursor: not-allowed; }

        .error-banner {
          margin-top: 15px;
          padding: 10px;
          background: #fff0f0;
          color: #cc0000;
          border: 1px solid #ffcdcd;
          border-radius: 4px;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        /* OUTPUTS */
        .results-stack {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .result-card { padding: 0; overflow: hidden; }

        .card-header {
          padding: 15px 20px;
          background: #f9f9f9;
          border-bottom: 1px solid #e5e5e5;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-header h4 {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .icon-btn {
          background: none;
          border: none;
          color: #606060;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }
        .icon-btn:hover { background: #e5e5e5; color: #0f0f0f; }

        /* TITLES LIST */
        .titles-list { padding: 10px 0; }
        .title-item {
          padding: 12px 20px;
          border-bottom: 1px solid #f2f2f2;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .title-item:last-child { border-bottom: none; }
        
        .idx {
          color: #aaa;
          font-size: 12px;
          font-weight: 700;
          min-width: 20px;
        }
        .text { flex: 1; font-weight: 500; font-size: 15px; }
        
        .copy-small {
          background: transparent;
          border: 1px solid #ddd;
          color: #606060;
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 4px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .title-item:hover .copy-small { opacity: 1; }
        .copy-small:hover { border-color: #065fd4; color: #065fd4; }

        /* BOXES & TAGS */
        .desc-box, .script-box { padding: 20px; }
        .desc-box pre, .script-box pre {
          white-space: pre-wrap;
          font-family: inherit;
          font-size: 14px;
          line-height: 1.6;
          color: #333;
          margin: 0;
        }

        .tags-cloud {
          padding: 20px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .tag-chip {
          background: #f2f2f2;
          color: #0f0f0f;
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        /* LOADING & EMPTY STATES */
        .loading-state, .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
          background: white;
          border-radius: 12px;
          border: 1px dashed #ccc;
          color: #606060;
        }
        .empty-icon, .pulse-icon {
          color: #ccc;
          margin-bottom: 15px;
        }
        .pulse-icon { color: #ff0000; animation: pulse 1s infinite; }
        @keyframes pulse { 0% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 0.8; } }

        h3 { margin: 0 0 8px 0; color: #0f0f0f; font-size: 18px; }
        p { margin: 0; font-size: 14px; color: #606060; }
      `}</style>
    </div>
  );
};

export default YouTubeGenerator;